import { useMemo } from 'react'

import { Button } from '@factorialco/deprecated-design-system'
import { BulkAssignToJobCatalogTreeCompetenciesCompetencyAssignmentsInput } from 'generated/resources/datatable.graphql'
import {
  CompetenciesCompetencyCrumbDocument,
  CompetenciesCompetencyLevelsAssignSidepanelBulkAssignDocument,
  CompetencyFragment,
  CompetencyLevelFragment,
  CompetencyRoleLevelsDataCollectionDocument,
  CompetencyRoleLevelsPageDocument,
} from 'generated/resources/graphql'
import { groupBy, mapValues, partition } from 'lodash'

import Sidepanel from 'modules/core/components/Sidepanel'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { RoleLevelAssignationForm } from 'modules/competencies/components/RoleLevelAssignationForm'

interface FormValues {
  assignments: BulkAssignToJobCatalogTreeCompetenciesCompetencyAssignmentsInput[]
}

interface Props {
  open: boolean
  competency: Pick<CompetencyFragment, 'id' | 'name'>
  competencyLevels: CompetencyLevelFragment[]
  excludeRoleLevelsUuids: string[]
  onClose: () => void
}

export const CompetencyAssignRoleLevelsSidepanel = ({
  open,
  competency,
  competencyLevels,
  excludeRoleLevelsUuids,
  onClose,
}: Props) => {
  const { company } = useCurrentSession()
  const [assignLevels] = useMutation(
    CompetenciesCompetencyLevelsAssignSidepanelBulkAssignDocument,
    {
      extractorFn: (data) =>
        data.competenciesMutations.bulkAssignToJobCatalogTreeCompetenciesCompetency,
    }
  )

  const form = useForm<FormValues>({
    defaultValues: { assignments: [] },
    onSubmit: async (values) => {
      const result = await assignLevels({
        variables: {
          companyId: company.id,
          assignments: values.assignments,
        },
        refetchQueries: [
          CompetencyRoleLevelsPageDocument,
          CompetenciesCompetencyCrumbDocument,
          CompetencyRoleLevelsDataCollectionDocument,
        ],
        awaitRefetchQueries: true,
      })

      const handled = handleResult(result, (errors) => form.setErrors(errors))

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
        handleClose()
      }

      return handled
    },
  })

  const formAssignments = useMemo(() => {
    const [levelAssignments, competencyAssignments] = partition(form.values.assignments, (a) =>
      Boolean(a.competencyLevelId)
    )

    return {
      competency: competencyAssignments.map((a) => a.nodeUuid),
      competencyLevels: mapValues(
        groupBy(levelAssignments, (a) => a.competencyLevelId),
        (assignments) => assignments.map((a) => a.nodeUuid)
      ),
    }
  }, [form.values.assignments])

  const handleClose = () => {
    form.resetForm()
    onClose()
  }

  const onAssignToCompetency = (uuids: string[]) => {
    form.setFieldValue(
      'assignments',
      uuids.map((uuid) => ({ competencyId: competency.id, nodeUuid: uuid }))
    )
  }

  const onAssignToCompetencyLevel = (competencyLevelId: string | number, uuids: string[]) => {
    const otherAssignments = form.values.assignments.filter(
      (a) => a.competencyLevelId !== competencyLevelId
    )

    const newAssignments = uuids.map((uuid) => ({
      competencyId: competency.id,
      competencyLevelId,
      nodeUuid: uuid,
    }))

    form.setFieldValue('assignments', [...otherAssignments, ...newAssignments])
  }

  return (
    <Sidepanel
      open={open}
      onClose={onClose}
      onCloseClicked={handleClose}
      width="wideXL"
      title={i18n.t('competencies_v3.levels.role_levels_data_collection.actions.assign_jobs')}
      footer={
        <>
          <Button onClick={handleClose} type="secondary">
            {i18n.t('common.cancel')}
          </Button>
          <Button
            onClick={() => form.handleSubmit()}
            loading={form.isSubmitting}
            disabled={form.values.assignments.length === 0}
          >
            {i18n.t('common.save')}
          </Button>
        </>
      }
    >
      <RoleLevelAssignationForm
        competencyName={competency.name}
        competencyLevels={competencyLevels}
        excludeUuids={excludeRoleLevelsUuids}
        assignments={formAssignments}
        onAssignToCompetency={onAssignToCompetency}
        onAssignToCompetencyLevel={onAssignToCompetencyLevel}
      />
    </Sidepanel>
  )
}
